/**
 * TEMP-H-003: Event Row Component
 * 
 * Displays individual timeline events with badges, expandable details,
 * and artifact links. Optimized for virtualized lists.
 */

import React, { useState, memo } from 'react';
import { 
  ChevronDown, 
  ChevronRight, 
  Clock, 
  Hash, 
  AlertTriangle, 
  CheckCircle,
  XCircle,
  Info,
  Copy
} from 'lucide-react';
import type { TimelineEvent } from '@/types/execution-events';
import { STAGE_DISPLAY_CONFIG, EVENT_TYPE_DISPLAY_CONFIG } from '@/types/execution-events';
import { ArtifactThumb } from './ArtifactThumb';
import MusicEventCard from './event-cards/MusicEventCard';
import QcEventCard from './event-cards/QcEventCard';
import BillingEventCard from './event-cards/BillingEventCard';
import ExportEventCard from './event-cards/ExportEventCard';

interface EventRowProps {
  event: TimelineEvent;
  className?: string;
  compact?: boolean;
  showArtifacts?: boolean;
}

export const EventRow = memo(function EventRow({ 
  event, 
  className = '',
  compact = false,
  showArtifacts = true
}: EventRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const stageConfig = event.stage ? STAGE_DISPLAY_CONFIG[event.stage] : null;
  const eventTypeConfig = EVENT_TYPE_DISPLAY_CONFIG[event.event_type];

  // Get theme-based colors for stage badges using design tokens
  const getStageTokenColors = (color: string) => {
    const colorMap = {
      blue: { bg: 'var(--accent)', bgAlpha: '0.2', text: 'var(--accent)' },
      purple: { bg: 'var(--color-accent-500)', bgAlpha: '0.2', text: 'var(--color-accent-400)' },
      green: { bg: 'var(--success)', bgAlpha: '0.2', text: 'var(--success)' },
      orange: { bg: 'var(--warning)', bgAlpha: '0.2', text: 'var(--warning)' },
      teal: { bg: 'var(--color-accent-500)', bgAlpha: '0.2', text: 'var(--color-accent-300)' },
      pink: { bg: 'var(--color-accent-500)', bgAlpha: '0.2', text: 'var(--color-accent-300)' },
      red: { bg: 'var(--danger)', bgAlpha: '0.2', text: 'var(--danger)' },
      indigo: { bg: 'var(--accent)', bgAlpha: '0.2', text: 'var(--accent)' },
      cyan: { bg: 'var(--accent)', bgAlpha: '0.2', text: 'var(--accent)' },
      emerald: { bg: 'var(--success)', bgAlpha: '0.2', text: 'var(--success)' },
      yellow: { bg: 'var(--warning)', bgAlpha: '0.2', text: 'var(--warning)' },
      violet: { bg: 'var(--accent)', bgAlpha: '0.2', text: 'var(--accent)' },
      slate: { bg: 'var(--fg-muted)', bgAlpha: '0.2', text: 'var(--fg-secondary)' },
      stone: { bg: 'var(--fg-muted)', bgAlpha: '0.2', text: 'var(--fg-secondary)' },
      amber: { bg: 'var(--warning)', bgAlpha: '0.2', text: 'var(--warning)' },
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getSeverityIcon = () => {
    switch (event.severity) {
      case 'success':
        return <CheckCircle className="w-4 h-4" style={{ color: 'var(--success)' }} data-testid="severity-success-icon" />;
      case 'error':
        return <XCircle className="w-4 h-4" style={{ color: 'var(--danger)' }} data-testid="severity-error-icon" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4" style={{ color: 'var(--warning)' }} data-testid="severity-warning-icon" />;
      default:
        return <Info className="w-4 h-4" style={{ color: 'var(--accent)' }} data-testid="severity-info-icon" />;
    }
  };

  const getSeverityBorderColor = () => {
    switch (event.severity) {
      case 'success':
        return 'var(--success)';
      case 'error':
        return 'var(--danger)';
      case 'warning':
        return 'var(--warning)';
      default:
        return 'var(--accent)';
    }
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatPayloadValue = (value: any): string => {
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const renderPayloadField = (key: string, value: any) => {
    const displayValue = formatPayloadValue(value);
    const isLongValue = displayValue.length > 100;
    
    return (
      <div key={key} className="flex items-start gap-2 p-2 rounded" style={{ backgroundColor: 'var(--bg-subtle)' }}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono" style={{ color: 'var(--accent)' }}>{key}</span>
            <button
              onClick={() => copyToClipboard(displayValue, key)}
              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white/10 rounded transition-all"
              title="Copy value"
            >
              <Copy className="w-3 h-3" style={{ color: 'var(--fg-muted)' }} />
            </button>
            {copiedField === key && (
              <span className="text-xs" style={{ color: 'var(--success)' }}>Copied!</span>
            )}
          </div>
          <div className={`text-xs font-mono ${
            isLongValue ? 'max-h-24 overflow-y-auto' : ''
          }`} style={{ color: 'var(--fg-secondary)' }}>
            <pre className="whitespace-pre-wrap break-words">{displayValue}</pre>
          </div>
        </div>
      </div>
    );
  };

  // Check if we should use specialized rendering
  const shouldUseSpecializedCard = () => {
    // Music stage events (original music + E8 music stages + future music sub-stages)
    if (event.event_type === 'content_ready' && event.stage?.startsWith('music')) {
      return 'music';
    }
    
    // Export preview events
    if (event.stage === 'export_preview' && event.event_type === 'content_ready') {
      return 'export';
    }
    
    // QC events
    if (event.event_type === 'qc_completed' || event.event_type === 'scores_ready') {
      return 'qc';
    }
    
    // Billing events
    if (event.event_type === 'billing_committed') {
      return 'billing';
    }
    
    return null;
  };

  const specializedCardType = shouldUseSpecializedCard();

  // Render specialized cards
  if (specializedCardType === 'music') {
    return (
      <MusicEventCard 
        event={event} 
        compact={compact} 
        showArtifacts={showArtifacts}
      />
    );
  }

  if (specializedCardType === 'qc') {
    return (
      <QcEventCard 
        event={event} 
        compact={compact}
      />
    );
  }

  if (specializedCardType === 'billing') {
    return (
      <BillingEventCard 
        event={event} 
        compact={compact}
      />
    );
  }

  if (specializedCardType === 'export') {
    return (
      <ExportEventCard 
        event={event} 
        compact={compact} 
        showArtifacts={showArtifacts}
      />
    );
  }

  // Default rendering for other event types
  return (
    <div 
      className={`group border-l-2 ${className}`}
      style={{
        borderLeftColor: getSeverityBorderColor(),
        backgroundColor: 'var(--bg-surface)'
      } as React.CSSProperties}
    >
      {/* Main Event Row */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Expand Toggle */}
          {event.isExpandable && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-1 p-0.5 rounded transition-colors hover:bg-white/10"
              style={{ 
                backgroundColor: 'var(--bg-interactive-idle)'
              }}
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} event details`}
            >
              {isExpanded ? (
                <ChevronDown className="w-3 h-3" style={{ color: 'var(--fg-muted)' }} />
              ) : (
                <ChevronRight className="w-3 h-3" style={{ color: 'var(--fg-muted)' }} />
              )}
            </button>
          )}

          {/* Event Icon */}
          <div className="mt-1 flex-shrink-0">
            {getSeverityIcon()}
          </div>

          {/* Event Content */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium truncate" style={{ color: 'var(--fg-primary)' }}>
                  {event.displayTitle}
                </h4>
                {event.displayDescription && (
                  <p className="text-xs mt-0.5" style={{ color: 'var(--fg-muted)' }}>
                    {event.displayDescription}
                  </p>
                )}
              </div>

              {/* Badges */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Stage Badge */}
                {stageConfig ? (
                  <span 
                    className="px-2 py-0.5 text-xs rounded-full"
                    style={{
                      backgroundColor: `color-mix(in srgb, ${getStageTokenColors(stageConfig.color).bg} ${getStageTokenColors(stageConfig.color).bgAlpha}, transparent)`,
                      color: getStageTokenColors(stageConfig.color).text
                    }}
                  >
                    {stageConfig.icon} {stageConfig.label}
                  </span>
                ) : (
                  <span className="px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: 'var(--bg-subtle)', color: 'var(--fg-secondary)' }}>
                    📋 {event.stage}
                  </span>
                )}

                {/* Event Type Badge */}
                {eventTypeConfig ? (
                  <span className="px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: 'var(--bg-elevated)', color: 'var(--fg-secondary)' }}>
                    {eventTypeConfig.icon} {eventTypeConfig.label}
                  </span>
                ) : (
                  <span className="px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: 'var(--bg-elevated)', color: 'var(--fg-secondary)' }}>
                    📋 {event.event_type}
                  </span>
                )}

                {/* Attempt Number */}
                {event.attemptNumber && (
                  <span 
                    className="px-2 py-0.5 text-xs rounded-full"
                    style={{
                      backgroundColor: 'color-mix(in srgb, var(--warning) 0.2, transparent)',
                      color: 'var(--warning)'
                    }}
                  >
                    #{event.attemptNumber}
                  </span>
                )}
              </div>
            </div>

            {/* Metadata */}
            <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--fg-muted)' }}>
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatTimestamp(event.timestamp)}
              </div>
              
              {event.correlation_id && (
                <div className="flex items-center gap-1">
                  <Hash className="w-3 h-3" />
                  <span className="font-mono">{event.correlation_id.slice(0, 8)}</span>
                </div>
              )}
            </div>

            {/* Artifacts */}
            {showArtifacts && event.artifactRefs.length > 0 && (
              <div className="mt-3">
                <h5 className="text-xs font-medium mb-2" style={{ color: 'var(--fg-muted)' }}>
                  Artifacts ({event.artifactRefs.length})
                </h5>
                <div className="flex items-center gap-2 overflow-x-auto">
                  {event.artifactRefs.map((artifact, index) => (
                    <ArtifactThumb 
                      key={`${artifact.id}-${index}`}
                      artifact={artifact}
                      size="sm"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Expanded Details */}
            {isExpanded && event.isExpandable && (
              <div className="mt-4 space-y-3">
                <div className="border-t pt-3" style={{ borderColor: 'var(--border-default)' }}>
                  <h5 className="text-xs font-medium mb-2" style={{ color: 'var(--fg-muted)' }}>Event Payload</h5>
                  <div className="space-y-2">
                    {Object.entries(event.payload).map(([key, value]) =>
                      renderPayloadField(key, value)
                    )}
                  </div>
                </div>

                {/* Technical Details */}
                <div className="border-t pt-3" style={{ borderColor: 'var(--border-default)' }}>
                  <h5 className="text-xs font-medium mb-2" style={{ color: 'var(--fg-muted)' }}>Technical Details</h5>
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <span style={{ color: 'var(--fg-muted)' }}>Event ID:</span>
                      <div className="font-mono break-all" style={{ color: 'var(--fg-secondary)' }}>{event.id}</div>
                    </div>
                    
                    <div>
                      <span style={{ color: 'var(--fg-muted)' }}>Execution ID:</span>
                      <div className="font-mono break-all" style={{ color: 'var(--fg-secondary)' }}>{event.execution_id}</div>
                    </div>
                    
                    {event.correlation_id && (
                      <div>
                        <span style={{ color: 'var(--fg-muted)' }}>Correlation ID:</span>
                        <div className="font-mono break-all" style={{ color: 'var(--fg-secondary)' }}>{event.correlation_id}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});